/*eslint-disable*/
import React, { useCallback, useEffect, useState } from 'react';
import { Img } from 'ui-kit';
import { CT_LOGO, INFO_ICON, NOTIFICATION_BELL_ICON } from 'assets/icons';
import { AuthEntity, CollectEntity, NotificationEntity } from '_entities';
import { IsAzAccount, IsIncludes } from 'modules';
import { useSelector, useDispatch } from 'react-redux';
import { NotificationModal, UserInformationModal } from 'modules/Modals';
import { useWindowDimensions, useIsReadyAndOffline, useRefreshNotificationsOnMediaExport } from 'hooks';
import { idbService, notification } from 'utils/services';
import { baseUrl } from 'lib/baseUrl';
import axios from 'axios';
import { URLS, USER_ROLE_TYPES } from '_constants';
import { size as BREAKPOINTS } from '_constants/DEVICES';
import { useLocation } from 'react-router-dom';
import { getMenuRoutes } from './_utils';
import * as Styled from './styles';
import MenuDesktop from './components/DesktopMenu';
import HeaderMobile from './components/MobileMenu';

const {
  getAuthenticatedUser,
  getUserEmail,
  getProfilePermissions,
  getModularityPermissions,
  getDashboardLinks,
  getAccountProductInfo,
  getUserRoleType,
  getCanAccessAppDashboard,
  getIsAppDashboardOnlyUser,
} = AuthEntity.selectors;
const { setCollectCount } = CollectEntity.actions;
const { getNotificationList } = NotificationEntity.actions;
const { getNotificationMessages, getUnreadMessages } = NotificationEntity.selectors;

const checkPollingTimeout = () => {
  const lastReportTime = localStorage.getItem('lastReportTime');
  if (!lastReportTime) return false;
  const last = Number(lastReportTime);
  if (!Number.isFinite(last)) return false;
  const tenMinutes = 10 * 60 * 1000;
  const expiresAt = last + tenMinutes;
  return Date.now() < expiresAt;
};

const hasPendedReports = () => {
  const generatedReports = JSON.parse(localStorage.getItem('generatedReports') || '[]');
  return generatedReports.length > 0;
};

const getGeneratedReports = async (taskId) => {
  const token = localStorage.getItem('Authorization');
  return axios.get(`${baseUrl}tasks/${taskId}/status/`, {
    headers: {
      Authorization: `Token ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/vnd.api+json',
    },
  });
};

const removeTaskIdFromStorage = (taskId) => {
  const generatedReports = JSON.parse(localStorage.getItem('generatedReports') || '[]');
  const remainingReports = generatedReports.filter((id) => id !== taskId);

  if (remainingReports.length === 0) {
    localStorage.removeItem('generatedReports');
    localStorage.removeItem('lastReportTime');
  } else {
    localStorage.setItem('generatedReports', JSON.stringify(remainingReports));
  }
};

const Header = () => {
  const dispatch = useDispatch();
  const { width: viewportWidth } = useWindowDimensions();
  const isReadyAndOffline = useIsReadyAndOffline();
  const isAuthenticated = useSelector(getAuthenticatedUser);
  const userEmail = useSelector(getUserEmail);
  const userPermissions = useSelector(getProfilePermissions);
  const userRoleType = useSelector(getUserRoleType);
  const canAccessAppDashboard = useSelector(getCanAccessAppDashboard);
  const isAppDashboardOnlyUser = useSelector(getIsAppDashboardOnlyUser);
  const modularityPermissions = useSelector(getModularityPermissions);
  const dashboardInfoSections = useSelector(getDashboardLinks);
  const notifications = useSelector(getNotificationMessages);
  const accountProduct = useSelector(getAccountProductInfo);
  const unreadMessages = useSelector(getUnreadMessages);
  const [isNotificationsModalOpened, setIsNotificationsModalOpened] = useState(false);
  const [isUserInfoModalOpened, setIsUserInfoModalOpened] = useState(false);
  const isAzAccount = IsAzAccount();
  const ROUTES = getMenuRoutes(
    isAuthenticated,
    userEmail,
    isReadyAndOffline,
    dashboardInfoSections,
    isAzAccount,
    canAccessAppDashboard,
    isAppDashboardOnlyUser,
  );
  const { pathname } = useLocation();

  const isAccManager = [USER_ROLE_TYPES.MULTI_ACCOUNT_MANAGER, USER_ROLE_TYPES.ACCOUNT_MANAGER].includes(userRoleType);

  const checkTaskStatus = async (taskId) => {
    try {
      const response = await getGeneratedReports(taskId);
      const status = response?.data?.data?.status;

      if (status === 'SUCCESS' || status === 'ERROR') {
        removeTaskIdFromStorage(taskId);
        await loadNotifications();

        if (status === 'SUCCESS') {
          notification.info({
            notification: `Your report is successfully generated and ready to download!`,
            onClick: handleNotificationClick,
            autoClose: { autoClose: false },
          });
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error(`Error checking task ${taskId}:`, error);
      return false;
    }
  };

  const menuItemHasAccess = (menuItem) => {
    const selectedPermission = menuItem.modularity ? modularityPermissions : userPermissions;
    return menuItem.permission
      ? IsIncludes(selectedPermission, menuItem.permission, menuItem?.isVisit, menuItem?.isDevider)
      : true;
  };

  const ROUTES_WITH_PERMISSIONS = ROUTES.map((menuItem) => {
    let newSubroutes = null;
    if (menuItem.subRoutes) {
      newSubroutes = menuItem.subRoutes.filter((subItem) => {
        return menuItemHasAccess(subItem);
      });
    }
    // P20M-1327 — a dropdown whose every sub-route was filtered out ("data" for
    // a GovRAMP-only Visitor) is dropped as well; parents that start out empty
    // (help without dashboard links) keep their current behaviour.
    if (menuItem.subRoutes?.length > 0 && newSubroutes.length === 0) return null;
    return menuItemHasAccess(menuItem) ? { ...menuItem, subRoutes: newSubroutes ? [...newSubroutes] : null } : null;
  }).filter((item) => item !== null);

  // this function will be moved to utils function on next release, to do
  const updateOfflineSurveyCount = async () => {
    const db = await idbService.initiateIDB();
    const count = await db.transaction('surveys').objectStore('surveys').count();
    await dispatch(setCollectCount(count));
  };

  const loadNotifications = useCallback(async () => {
    try {
      await dispatch(getNotificationList());
    } catch (e) {
      console.log('error', e);
    }
  }, [dispatch]);

  // Keeps the bell current when a media export lands while the user stays on this page.
  useRefreshNotificationsOnMediaExport(loadNotifications, isAuthenticated);

  const handleInfoClick = () => {
    setIsUserInfoModalOpened(() => true);
  };

  const closeUserInfoModal = () => {
    setIsUserInfoModalOpened(() => false);
  };

  const handleNotificationClick = () => {
    setIsNotificationsModalOpened((prev) => !prev);
  };

  const closeNotificationModal = () => {
    setIsNotificationsModalOpened(() => false);
  };

  useEffect(() => {
    if (unreadMessages && !isNotificationsModalOpened && isAuthenticated && pathname === URLS.dashboard) {
      const messages = unreadMessages > 1 ? 'messages' : 'message';
      notification.info({
        notification: `You have ${unreadMessages} unread ${messages}!`,
        onClick: handleNotificationClick,
      });
    }
  }, [unreadMessages, isNotificationsModalOpened, isAuthenticated]);

  useEffect(() => {
    updateOfflineSurveyCount();
    if (isAuthenticated) {
      loadNotifications();
    }
  }, []);

  useEffect(() => {
    let pollingInterval = null;

    const startPolling = async () => {
      if (!checkPollingTimeout()) {
        localStorage.removeItem('generatedReports');
        localStorage.removeItem('lastReportTime');
        return;
      }

      if (!hasPendedReports()) {
        return;
      }
      const generatedReports = JSON.parse(localStorage.getItem('generatedReports') || '[]');
      await Promise.all(generatedReports.map((taskId) => checkTaskStatus(taskId)));
      if (!hasPendedReports()) {
        if (pollingInterval) clearInterval(pollingInterval);
      }
    };

    if (isAuthenticated && checkPollingTimeout()) {
      startPolling();
      pollingInterval = setInterval(startPolling, 20000);
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [isAuthenticated]);

  return (
    <Styled.HeaderContainer className="no-print">
      <Img alt="ct logo" src={CT_LOGO} size={[150, 54]} />
      {accountProduct && <Styled.AccountProduct>{accountProduct}</Styled.AccountProduct>}
      {viewportWidth < parseInt(BREAKPOINTS.header, 10) ? (
        <>
          <HeaderMobile routes={ROUTES_WITH_PERMISSIONS} />
          <Styled.Spacer />
        </>
      ) : (
        <MenuDesktop routes={ROUTES_WITH_PERMISSIONS} />
      )}
      {isAuthenticated && isAccManager && viewportWidth >= parseInt(BREAKPOINTS.header, 10) && (
        <Styled.ImageWrapper padding="0 10px" onClick={handleInfoClick}>
          <Img tabIndex={0} id={1} size={[20, 20]} src={INFO_ICON} aria-label="userInfo" alt="userInfo" />
        </Styled.ImageWrapper>
      )}
      {isAuthenticated && (
        <Styled.ImageWrapper padding="0px 20px 0 10px" onClick={handleNotificationClick}>
          <Img
            tabIndex={0}
            id={1}
            size={[20, 20]}
            src={NOTIFICATION_BELL_ICON}
            aria-label="notification"
            alt="notification"
          />
          {Boolean(unreadMessages) && <Styled.Counter>{unreadMessages <= 9 ? unreadMessages : '9+'}</Styled.Counter>}
        </Styled.ImageWrapper>
      )}
      {isNotificationsModalOpened && (
        <NotificationModal
          onClose={closeNotificationModal}
          notificationList={notifications}
          loadNotifications={loadNotifications}
        />
      )}
      <UserInformationModal isOpen={isUserInfoModalOpened} onClose={closeUserInfoModal} />
    </Styled.HeaderContainer>
  );
};

export default Header;
